import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Check,
  Loader2,
  Search,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import { api } from "@/lib/api";
import { Badge, Button, Card, Skeleton ,
  Input,
} from "@/components/ui";
import { cn } from "@/lib/utils";
import { useServerText } from "@/i18n";
import { useFormat } from "@/i18n/format";

/** USD with cents, in the interface's number style ("$3.00" / "3,00 $US"). */
const PRICE: Intl.NumberFormatOptions = {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
};

/**
 * Workspace settings: the model credential, and the bot's display name.
 *
 * The key field is write-only by design. A stored key is shown as a
 * four-character hint and nothing more — the API has no endpoint that returns
 * one, so there is nothing here that could leak it.
 */
export function Settings() {
  const { t } = useTranslation(["settings", "common"]);
  const st = useServerText();
  const f = useFormat();
  const queryClient = useQueryClient();
  const [provider, setProvider] = useState<"anthropic" | "openrouter">("anthropic");
  const [apiKey, setApiKey] = useState("");
  const [model, setModel] = useState("");
  const [modelFilter, setModelFilter] = useState("");
  const [saved, setSaved] = useState(false);
  /**
   * Removal is confirmed inline rather than with a dialog.
   *
   * It is reversible — they can paste the key again — but it does stop every AI
   * feature in the workspace, so a stray click should not do it.
   */
  const [confirmRemove, setConfirmRemove] = useState(false);


  const settings = useQuery({ queryKey: ["settings"], queryFn: api.getSettings });

  const models = useQuery({
    queryKey: ["models", provider],
    queryFn: () => api.getModels(provider),
    staleTime: 60 * 60 * 1000,
    retry: false,
  });

  const save = useMutation({
    mutationFn: () => api.saveCredential({ provider, apiKey, model }),
    onSuccess: () => {
      setApiKey("");
      setSaved(true);
      setTimeout(() => setSaved(false), 4000);
      void queryClient.invalidateQueries({ queryKey: ["settings"] });
    },
  });

  const remove = useMutation({
    mutationFn: () => api.removeCredential(provider),
    onSuccess: () => {
      setConfirmRemove(false);
      void queryClient.invalidateQueries({ queryKey: ["settings"] });
    },
  });

  // OpenRouter returns hundreds of models. Filtering happens here rather than
  // server-side because the list is cached for an hour and typing should be
  // instant.
  const filtered = useMemo(() => {
    const all = models.data ?? [];
    const needle = modelFilter.trim().toLowerCase();
    const matched = needle
      ? all.filter(
          (m) =>
            m.id.toLowerCase().includes(needle) ||
            m.name.toLowerCase().includes(needle),
        )
      : all;
    // Free models first — it is the first thing a team evaluating this looks for.
    return [...matched].sort((a, b) => Number(b.isFree) - Number(a.isFree)).slice(0, 60);
  }, [models.data, modelFilter]);

  const current = settings.data?.credentials.find((c) => c.provider === provider);

  return (
    <div className="mx-auto max-w-[720px] space-y-6">
      <div>
        <h1 className="text-lg font-semibold tracking-tight text-text">{t("workspace.title")}</h1>
        <p className="mt-1 text-[13.5px] text-text-muted">
          {t("workspace.intro")}
        </p>
      </div>

      <Card className="p-5">
        <h2 className="text-[15px] font-semibold text-text">{t("workspace.provider.title")}</h2>

        <div className="mt-4 flex gap-2">
          {(["anthropic", "openrouter"] as const).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => {
                setProvider(option);
                setModel("");
                setModelFilter("");
              }}
              className={cn(
                "flex-1 rounded-lg border px-4 py-3 text-left transition-colors",
                provider === option
                  ? "border-accent bg-accent-subtle"
                  : "border-border hover:bg-surface-hover",
              )}
            >
              <span className="block text-[14px] font-medium text-text capitalize">
                {option}
              </span>
              <span className="mt-0.5 block text-[12.5px] text-text-muted">
                {option === "anthropic"
                  ? t("workspace.provider.anthropicHint")
                  : t("workspace.provider.openrouterHint")}
              </span>
            </button>
          ))}
        </div>

        {settings.isLoading ? (
          <Skeleton className="mt-4 h-10 w-full" />
        ) : (
          <div className="mt-5 space-y-4">
            <label className="block">
              <span className="mb-1.5 flex items-center gap-2 text-[13px] font-medium text-text">
                {t("workspace.apiKey.label")}
                {current ? (
                  <>
                    <Badge tone="success">
                      <Check size={11} /> {t("workspace.apiKey.ends", { hint: current.keyHint })}
                    </Badge>
                    <button
                      type="button"
                      onClick={() => setConfirmRemove((v) => !v)}
                      className="ml-auto inline-flex items-center gap-1 text-[12.5px] font-normal text-text-faint transition-colors hover:text-danger"
                    >
                      <Trash2 size={12} />
                      {t("workspace.apiKey.remove")}
                    </button>
                  </>
                ) : null}
              </span>
              <input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={current ? t("workspace.apiKey.replacePlaceholder") : "sk-…"}
                autoComplete="off"
                className="h-10 w-full rounded-lg border border-border bg-bg px-3 font-mono text-sm text-text placeholder:font-sans placeholder:text-text-faint focus:border-accent focus:outline-none"
              />
              <span className="mt-1.5 block text-[12px] text-text-faint">
                {t("workspace.apiKey.help", { provider })}
              </span>
            </label>

            <div>
              <span className="mb-1.5 block text-[13px] font-medium text-text">
                {t("workspace.model.label")}
              </span>

              {models.isLoading ? (
                <Skeleton className="h-10 w-full" />
              ) : models.error ? (
                <p className="rounded-lg bg-bg-subtle px-3 py-2 text-[13px] text-text-muted">
                  {t("workspace.model.needsKey", { provider })}
                </p>
              ) : (
                <>
                  {provider === "openrouter" ? (
                    <div className="relative mb-2">
                      <Search
                        size={14}
                        className="absolute top-1/2 left-3 -translate-y-1/2 text-text-faint"
                      />
                      <input
                        value={modelFilter}
                        onChange={(e) => setModelFilter(e.target.value)}
                        placeholder={t("workspace.model.filter", { count: models.data?.length ?? 0 })}
                        className="h-9 w-full rounded-lg border border-border bg-bg pr-3 pl-9 text-[13px] text-text placeholder:text-text-faint focus:border-accent focus:outline-none"
                      />
                    </div>
                  ) : null}

                  <div className="max-h-[280px] space-y-1 overflow-y-auto rounded-lg border border-border p-1">
                    {filtered.map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => setModel(m.id)}
                        className={cn(
                          "block w-full rounded-md px-2.5 py-2 text-left transition-colors",
                          model === m.id ? "bg-accent-subtle" : "hover:bg-surface-hover",
                        )}
                      >
                        <div className="flex items-baseline justify-between gap-3">
                          <span className="truncate text-[13px] font-medium text-text">
                            {m.name}
                          </span>
                          {m.isFree ? (
                            <Badge tone="success">{t("workspace.model.free")}</Badge>
                          ) : m.inputPricePerMTok !== null ? (
                            <span className="shrink-0 font-mono text-[11px] tabular-nums text-text-faint">
                              {f.number(m.inputPricePerMTok, PRICE)} /{" "}
                              {m.outputPricePerMTok != null ? f.number(m.outputPricePerMTok, PRICE) : "$—"}
                            </span>
                          ) : null}
                        </div>
                        {m.contextWindow ? (
                          <span className="mt-0.5 block text-[11.5px] text-text-faint">
                            {t("workspace.model.context", { size: f.number(Math.round(m.contextWindow / 1000)) })}
                          </span>
                        ) : null}
                      </button>
                    ))}
                  </div>
                  <p className="mt-1.5 text-[11.5px] text-text-faint">
                    {t("workspace.model.pricing")}
                  </p>
                </>
              )}
            </div>

            {confirmRemove ? (
              <div className="rounded-lg border border-danger/40 bg-danger-subtle/40 p-3.5">
                <p className="text-[13px] leading-relaxed text-text">
                  {t("workspace.removeConfirm.question")}
                </p>
                <p className="mt-1.5 text-[12.5px] leading-relaxed text-text-muted">
                  {t("workspace.removeConfirm.kept")}
                </p>
                <div className="mt-3 flex gap-2">
                  <Button
                    variant="danger"
                    size="sm"
                    disabled={remove.isPending}
                    onClick={() => remove.mutate()}
                  >
                    {remove.isPending ? (
                      <Loader2 size={13} className="animate-spin" />
                    ) : null}
                    {t("workspace.apiKey.remove")}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setConfirmRemove(false)}
                  >
                    {t("workspace.removeConfirm.keep")}
                  </Button>
                </div>
                {remove.error ? (
                  <p role="alert" className="mt-2.5 text-[12.5px] text-danger">
                    {remove.error instanceof Error
                      ? st(remove.error.message)
                      : t("workspace.removeConfirm.failed")}
                  </p>
                ) : null}
              </div>
            ) : null}

            {save.error ? (
              <p
                role="alert"
                className="rounded-lg bg-danger-subtle px-3 py-2 text-[13px] text-danger"
              >
                {save.error instanceof Error
                  ? st(save.error.message)
                  : t("workspace.saveFailed")}
              </p>
            ) : null}

            <div className="flex items-center gap-3">
              <Button
                variant="primary"
                disabled={!apiKey || !model || save.isPending}
                onClick={() => save.mutate()}
              >
                {save.isPending ? <Loader2 size={15} className="animate-spin" /> : null}
                {t("workspace.saveCredential")}
              </Button>
              {saved ? (
                <span className="inline-flex items-center gap-1.5 text-[13px] text-success">
                  <ShieldCheck size={14} /> {t("workspace.savedVerified")}
                </span>
              ) : null}
            </div>
          </div>
        )}
      </Card>

      <WorkspaceSettings
        botDisplayName={settings.data?.botDisplayName ?? "Meeting Notetaker"}
        retentionDays={settings.data?.retentionDays ?? null}
      />
    </div>
  );
}

/**
 * The two workspace settings that had an endpoint and no UI.
 *
 * The notetaker's name was printed as static text directly under copy telling
 * the reader to make it recognisable, and retention was fetched by the client
 * and never rendered — so a tenant could not act on the one setting that
 * deletes their data.
 */
function WorkspaceSettings({
  botDisplayName,
  retentionDays,
}: {
  botDisplayName: string;
  retentionDays: string | null;
}) {
  const { t } = useTranslation(["settings", "common"]);
  const queryClient = useQueryClient();
  const [name, setName] = useState(botDisplayName);
  const [days, setDays] = useState(retentionDays ?? "");
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: () =>
      api.updateSettings({
        botDisplayName: name.trim() || undefined,
        // Empty means "keep forever", which is a different instruction from
        // "zero days" — hence null rather than 0.
        retentionDays: days.trim() === "" ? null : Number(days),
      }),
    onSuccess: () => {
      setSaved(true);
      setTimeout(() => setSaved(false), 4000);
      void queryClient.invalidateQueries({ queryKey: ["settings"] });
    },
  });

  const invalidDays =
    days.trim() !== "" && (!Number.isInteger(Number(days)) || Number(days) < 1);

  return (
    <Card className="p-5">
      <h2 className="text-[15px] font-semibold text-text">{t("workspace.general.title")}</h2>

      <label className="mt-4 block">
        <span className="mb-1.5 block text-[13px] font-medium text-text">
          {t("workspace.general.botName")}
        </span>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("workspace.general.botNamePlaceholder")}
          className="max-w-[320px]"
        />
        <span className="mt-1.5 block text-[12px] leading-relaxed text-text-faint">
          {t("workspace.general.botNameHelp")}
        </span>
      </label>

      <label className="mt-4 block">
        <span className="mb-1.5 block text-[13px] font-medium text-text">
          {t("workspace.general.retention")}
        </span>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            min={1}
            value={days}
            onChange={(e) => setDays(e.target.value)}
            placeholder={t("workspace.general.retentionPlaceholder")}
            className="max-w-[120px]"
          />
          <span className="text-[13px] text-text-muted">{t("workspace.general.days")}</span>
        </div>
        <span
          className={cn(
            "mt-1.5 block text-[12px] leading-relaxed",
            invalidDays ? "text-warning" : "text-text-faint",
          )}
        >
          {invalidDays
            ? t("workspace.general.retentionInvalid")
            : t("workspace.general.retentionHelp")}
        </span>
      </label>

      <div className="mt-4 flex items-center gap-3">
        <Button
          variant="primary"
          disabled={save.isPending || invalidDays}
          onClick={() => save.mutate()}
        >
          {save.isPending ? <Loader2 size={15} className="animate-spin" /> : null}
          {t("common:actions.save")}
        </Button>
        {saved ? (
          <span className="text-[13px] text-success">{t("workspace.general.saved")}</span>
        ) : null}
      </div>
    </Card>
  );
}
